import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Popover } from 'react-native-popper'

import styled from 'styled-components/native'

import { AccessibilityRolesNative, IS_WEB } from '../../constants'
import { tID } from '../../utils/utils'
import { DropdownMenu } from '../formElements/typeAhead/DropdownMenu'
import { CalendarEmptyIcon } from '../icons/CalendarEmptyIcon'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'

type MonthYearDropdownMenuProps = {
  options: string[]
  selectedOption: string
  onOptionSelected: (dateString: string) => void
  testID?: string
}

const Container = styled.View(({ theme }) => ({
  flexDirection: 'row',
  marginLeft: theme.spacing['4px'],
  marginTop: theme.spacing['2px'],
}))

const DropdownContainer = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  padding: `${theme.spacing['8px']} 0`,
  borderRadius: theme.spacing['8px'],
  boxShadow: `0 2px 16px ${theme.colors.shadowLow}`,
  width: '260px',
  height: 'fit-content',
  maxHeight: '340px',
  overflow: 'scroll',
}))

const IconButton = styled(PressableOpacity)(({ theme }) => ({
  padding: theme.spacing['8px'],
}))

export const MonthYearDropdownMenu: React.FC<MonthYearDropdownMenuProps> = ({
  options,
  selectedOption,
  onOptionSelected,
  testID = 'MonthYearDropdownMenu',
}) => {
  const { formatMessage } = useIntl()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const triggerRef = useRef(null)

  return (
    <Container testID={tID(testID)}>
      <IconButton
        onPress={() => setIsDropdownOpen((prev) => !prev)}
        testID={tID('MonthYearDropdownMenu-button')}
        pressableRef={triggerRef}
        accessibilityLabel={formatMessage({
          defaultMessage: 'Select month and year',
          description: 'Accessibility label for month year dropdown button',
        })}
        accessibilityRole={IS_WEB ? 'button' : AccessibilityRolesNative.BUTTON}
      >
        <CalendarEmptyIcon width={25} height={23} />
      </IconButton>
      <Popover
        isOpen={isDropdownOpen}
        onOpenChange={setIsDropdownOpen}
        trigger={triggerRef}
        placement='right bottom'
        animated={!window.Cypress}
      >
        <Popover.Backdrop />
        <Popover.Content>
          <DropdownContainer testID={tID('MonthYearDropdownMenu-dropdown')}>
            <DropdownMenu
              options={options}
              onOptionPress={(dateString) => {
                onOptionSelected(dateString)
                setIsDropdownOpen(false)
              }}
              value={[selectedOption]}
              selectedItemStyle={null}
            />
          </DropdownContainer>
        </Popover.Content>
      </Popover>
    </Container>
  )
}
